import React, { createContext, useContext, useState, useMemo } from 'react';
import { AdminThemeTokens, ADMIN_DEFAULT_THEME, STORAGE_KEYS, tokensToCssVars } from './tokens';

interface AdminThemeContextType {
  theme: AdminThemeTokens;
  updateTheme: (partial: Partial<AdminThemeTokens>) => void;
  resetTheme: () => void;
}

const AdminThemeContext = createContext<AdminThemeContextType>({
  theme: ADMIN_DEFAULT_THEME,
  updateTheme: () => {},
  resetTheme: () => {},
});

export const useAdminTheme = () => useContext(AdminThemeContext);

interface AdminThemeProviderProps {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}

export const AdminThemeProvider: React.FC<AdminThemeProviderProps> = ({
  children,
  className = '',
  style,
}) => {
  const [theme, setTheme] = useState<AdminThemeTokens>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.ADMIN_THEME);
      if (stored) {
        return { ...ADMIN_DEFAULT_THEME, ...JSON.parse(stored) };
      }
    } catch {
      // Fallback to default
    }
    return ADMIN_DEFAULT_THEME;
  });

  const updateTheme = (partial: Partial<AdminThemeTokens>) => {
    setTheme((prev) => {
      const next = { ...prev, ...partial };
      try {
        localStorage.setItem(STORAGE_KEYS.ADMIN_THEME, JSON.stringify(next));
      } catch {
        // Storage failure fallback
      }
      return next;
    });
  };

  const resetTheme = () => {
    setTheme(ADMIN_DEFAULT_THEME);
    try {
      localStorage.removeItem(STORAGE_KEYS.ADMIN_THEME);
    } catch {
      // Storage fallback
    }
  };

  const cssStyle = useMemo(() => {
    return {
      ...tokensToCssVars(theme as unknown as Record<string, string>),
      ...style,
    };
  }, [theme, style]);

  return (
    <AdminThemeContext.Provider value={{ theme, updateTheme, resetTheme }}>
      <div className={`admin-shell ${className}`} style={cssStyle}>
        {children}
      </div>
    </AdminThemeContext.Provider>
  );
};
